<template>
  <div class="scroll-container" :id="name">
    <div
      class="prev"
      @click="scrollLeft"
      v-if="hasHorizontalScroll"
      v-show="showPrev"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 5 8"
        fill="none"
      >
        <path d="M1.5 7L4.5 4L1.5 1" stroke="#888888" stroke-linecap="square" />
      </svg>
    </div>
    <div
      class="items-container"
      ref="itemsContainer"
      @mousedown="handleMouseDown"
      @mouseleave="handleMouseLeave"
      @mouseup="handleMouseUp"
      @mousemove="handleMouseMove"
      @touchstart="startTouch"
      @touchmove="moveTouch"
      @touchend="endTouch"
    >
      <slot></slot>
    </div>

    <div
      class="next"
      @click="scrollRight"
      v-if="hasHorizontalScroll"
      v-show="showNext"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="10"
        viewBox="0 0 5 8"
        fill="none"
      >
        <path d="M1.5 7L4.5 4L1.5 1" stroke="#888888" stroke-linecap="square" />
      </svg>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, Ref, watch } from 'vue'
import useDeviceStore from '@/store/device'

const props = withDefaults(
  defineProps<{
    name?: string
    prefixItemId?: string
    nextItemsNumber?: number
    numberItems: number
  }>(),
  {
    name: 'horizontal',
    prefixItemId: 'item',
    numberItems: 0,
    nextItemsNumber: 2,
  }
)
const deviceStore = useDeviceStore()

const currentIndex = ref(0)
const showNext = ref(true)
const showPrev = ref(true)
const hasHorizontalScroll = ref(false)

const itemsContainer: Ref<HTMLElement | null> = ref(null)

onMounted(() => {
  watch(
    () => props.numberItems,
    () => {
      setTimeout(() => {
        checkElementHasScrollable()
        if (itemsContainer.value) checkShowScrollButton(itemsContainer.value)
      })
    },
    { immediate: true }
  )

  if (itemsContainer.value) {
    itemsContainer.value.classList.remove('active')
  }
})

function scrollLeft(): void {
  if (currentIndex.value > 0) {
    currentIndex.value -= props.nextItemsNumber
    if (currentIndex.value < 0) currentIndex.value = 0

    scrollTo(`${props.prefixItemId}-${currentIndex.value}`)

    // wait a few time for scrcolled
    setTimeout(() => {
      if (itemsContainer.value) checkShowScrollButton(itemsContainer.value)
    }, 250)
  }
}

function scrollRight(): void {
  if (currentIndex.value < props.numberItems - 1) {
    currentIndex.value += props.nextItemsNumber
    if (currentIndex.value > props.numberItems - 1)
      currentIndex.value = props.numberItems - 1
    scrollTo(`${props.prefixItemId}-${currentIndex.value}`)

    // wait a few time for scrcolled
    setTimeout(() => {
      if (itemsContainer.value) checkShowScrollButton(itemsContainer.value)
    }, 250)
  }
}

function scrollToCurrent(): void {
  scrollTo(`${props.prefixItemId}-${currentIndex.value}`)
}

function scrollTo(itemId: string) {
  const scrollContainer = document.querySelector(`#${props.name}`)
  if (!scrollContainer || !itemsContainer.value) return
  const scrollTo = scrollContainer.querySelector(`#${itemId}`)?.offsetLeft

  itemsContainer.value.scrollTo({
    left: (scrollTo || 0) - 25,
    behavior: 'smooth',
  })
}

let isDown = false
let desktopStartX = 0
let lastScrollLeft = 0

const handleMouseDown = (e: MouseEvent) => {
  if (deviceStore.isMobile) return
  if (!itemsContainer.value) return
  isDown = true
  itemsContainer.value.classList.add('active')
  desktopStartX = e.pageX - itemsContainer.value.offsetLeft
  lastScrollLeft = itemsContainer.value.scrollLeft
}

const handleMouseLeave = () => {
  if (deviceStore.isMobile) return
  if (!itemsContainer.value) return
  isDown = false
  itemsContainer.value.classList.remove('active')

  scrollToCurrent()
}

const handleMouseUp = () => {
  if (deviceStore.isMobile) return
  if (!itemsContainer.value) return
  isDown = false
  itemsContainer.value.classList.remove('active')
  const deltaX = itemsContainer.value.scrollLeft - lastScrollLeft

  if (itemsContainer.value.scrollLeft > lastScrollLeft && deltaX > 50) {
    scrollRight()
  } else if (itemsContainer.value.scrollLeft < lastScrollLeft && deltaX < -50) {
    scrollLeft()
  } else {
    scrollToCurrent()
  }
}

const handleMouseMove = (e: MouseEvent) => {
  if (deviceStore.isMobile) return
  if (!isDown || !itemsContainer.value) return
  e.preventDefault()
  const x = e.pageX - itemsContainer.value.offsetLeft
  const walk = x - desktopStartX
  itemsContainer.value.scrollLeft = lastScrollLeft - walk
}

let mobileStartX = 0
let isSwiping = false

const startTouch = (e: TouchEvent) => {
  if (!deviceStore.isMobile) return

  const touch = e.touches[0]
  mobileStartX = touch.pageX
  isSwiping = true
}

const moveTouch = () => {
  if (!deviceStore.isMobile) return
  if (!isSwiping) return
}

const endTouch = (e: TouchEvent) => {
  if (!deviceStore.isMobile) return
  if (!isSwiping) return
  const touch = e.changedTouches[0]
  const endX = touch.pageX

  const deltaX = endX - mobileStartX

  if (endX < mobileStartX && deltaX < -50) {
    scrollRight()
  } else if (mobileStartX < endX && deltaX > 50) {
    scrollLeft()
  } else {
    scrollToCurrent()
  }

  isSwiping = false
}

function checkShowScrollButton(element: Element | HTMLElement) {
  const maxScrollLeft = element.scrollWidth - element.clientWidth
  if (element.scrollLeft >= maxScrollLeft - 30) {
    showNext.value = false
  } else {
    showNext.value = true
  }

  if (element.scrollLeft <= 0 || currentIndex.value == 0) {
    showPrev.value = false
  } else {
    showPrev.value = true
  }
}

function checkElementHasScrollable() {
  if (!itemsContainer.value) return
  const element = itemsContainer.value
  hasHorizontalScroll.value = element.scrollWidth > element.clientWidth
}
</script>

<style lang="scss">
.scroll-container {
  overflow: hidden;
  display: flex;
  align-items: center;
  position: relative;

  .items-container {
    min-height: 40px;
    display: flex;
    overflow-x: auto;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  .items-container.active {
    cursor: ew-resize;
  }

  .prev,
  .next {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    aspect-ratio: 1;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border: 1px solid #888888;
    border-radius: 50%;
    text-align: center;
    cursor: ew-resize;
    background: #fff;
  }
  .prev {
    left: 0;
    svg {
      transform: rotate(180deg);
    }
  }
  .next {
    right: 0;
  }

  .selected-filters__option {
    user-select: none;
  }
}
</style>
